// src/shared/hooks/useScrollDirection.js - Enhanced for route changes
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const useScrollDirection = (threshold = 10) => {
  const [scrollDirection, setScrollDirection] = useState('up');
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const prevScrollY = useRef(0);
  const ticking = useRef(false);
  const location = useLocation();

  // Reset scroll state when route changes
  const resetScrollState = useCallback(() => {
    const currentScrollY =
      window.pageYOffset || document.documentElement.scrollTop;
    prevScrollY.current = currentScrollY;
    setScrollY(currentScrollY);
    setScrollDirection('up');

    // Show navigation if at top, hide if scrolled down
    setIsVisible(currentScrollY <= 100);
  }, []);

  // Reset on route change
  useEffect(() => {
    // Small delay to ensure page has loaded
    const timer = setTimeout(resetScrollState, 50);
    return () => clearTimeout(timer);
  }, [location.pathname, resetScrollState]);

  useEffect(() => {
    const updateScrollDirection = () => {
      const currentScrollY =
        window.pageYOffset || document.documentElement.scrollTop;
      const direction = currentScrollY > prevScrollY.current ? 'down' : 'up';
      const distance = Math.abs(currentScrollY - prevScrollY.current);

      // Only update if we've scrolled past the threshold
      if (distance >= threshold) {
        setScrollDirection(direction);
        setScrollY(currentScrollY);

        // Navigation visibility logic
        if (direction === 'down' && currentScrollY > 100) {
          setIsVisible(false);
        } else if (direction === 'up' || currentScrollY <= 100) {
          setIsVisible(true);
        }

        prevScrollY.current = currentScrollY;
      }

      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        ticking.current = requestAnimationFrame(updateScrollDirection);
      }
    };

    // Set initial state
    resetScrollState();

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (ticking.current) {
        cancelAnimationFrame(ticking.current);
      }
    };
  }, [threshold, resetScrollState]);

  return {
    scrollDirection,
    scrollY,
    isVisible,
  };
};

export default useScrollDirection;
